import { ENV } from "@/axios/constants";
import { axiosInstance } from "@/axios/interceptors";
import { CC_TOKEN_TYPE } from "@/constants/common-constants";
import { ZodSentryReporter } from "@/lib/zod-sentry-reporter";
import { z } from "zod";

const GUEST_CC_TOKEN_TYPE = {
	global_guest_cc_token_type: CC_TOKEN_TYPE.BANK_ACCOUNT,
};

export const BankAccountSchema = z.object({
	id: z.number(),
	cc_token: z.string(),
	card_type: z.string().nullable(),
	card_holder: z.string().nullable(),
	bank_name: z.string().nullable(),
	bank_account: z.string().nullable(),
	bank_routing: z.string().nullable(),
	is_disabled: z.union([z.literal(0), z.literal(1)]).optional(),
	default: z.union([z.literal(0), z.literal(1)]),
});

export const BankAccountListSchema = z.array(BankAccountSchema);
export type BankAccount = z.infer<typeof BankAccountSchema>;
export type BankAccountList = z.infer<typeof BankAccountListSchema>;

export type GetGuestBankAccountsParams = {
	guestId: number;
	locationId: number;
};

export async function getGuestBankAccounts({
	guestId,
	locationId,
}: GetGuestBankAccountsParams) {
	const userId = ENV.VITE_USER_ID;

	const response = await axiosInstance.get(
		`/locations/${locationId}/guests/${guestId}/guest_cc_token`,
		{
			params: {
				guest_id: guestId,
				user_id: userId,
				...GUEST_CC_TOKEN_TYPE,
			},
		},
	);
	return ZodSentryReporter.parse(BankAccountListSchema, response);
}

// Schema for adding a guest credit card (based on the provided API response)
export const AddGuestBankAccountResponseSchema = z.object({
	id: z.number(),
});

export type AddGuestBankAccountResponse = z.infer<
	typeof AddGuestBankAccountResponseSchema
>;

export type AddGuestBankAccountParams = {
	cc_token: string;
	card_holder: string;
	flow?: string;
	default?: boolean;
	bank_account: string;
	bank_routing: string;
	bank_name: string;
	reusable?: number;
	guest_id: number;
	locationId: number;
};

export async function addGuestBankAccount({
	cc_token,
	card_holder,
	flow = "profile",
	default: isDefault = false,
	bank_account,
	bank_routing,
	bank_name,
	reusable = 1,
	guest_id,
	locationId,
}: AddGuestBankAccountParams) {
	const userId = ENV.VITE_USER_ID;
	const response = await axiosInstance.post(
		`/locations/${locationId}/guest_cc_token`,
		{
			cc_token,
			card_holder,
			flow,
			default: isDefault,
			bank_account,
			bank_routing,
			bank_name,
			reusable,
			guest_id,
			user_id: userId,
			...GUEST_CC_TOKEN_TYPE,
		},
	);
	return ZodSentryReporter.parse(AddGuestBankAccountResponseSchema, response);
}

export type DeleteGuestBankAccountParams = {
	guestId: number;
	bankAccountId: number;
	locationId: number;
};

export async function deleteGuestBankAccount({
	guestId,
	bankAccountId,
	locationId,
}: DeleteGuestBankAccountParams) {
	const userId = ENV.VITE_USER_ID;

	const response = await axiosInstance.delete(
		`/locations/${locationId}/guests/${guestId}/guest_cc_token/${bankAccountId}`,
		{
			params: {
				user_id: userId,
				...GUEST_CC_TOKEN_TYPE,
			},
		},
	);
	const parsed = ZodSentryReporter.safeParse(BankAccountSchema, response);
	return parsed.success ? parsed.data : response.data;
}
